
import { isAppUsing } from 'bits/flip'
import { Component, defineAsyncComponent, Getter, namespace, Prop, State, Vue } from 'bits/vue'
import { OzTextButtonColorScheme } from 'library/v4/components/OzTextButton.vue'
import type { Cid, Comment, CommentId, Post, User } from 'types'
import AvatarImage from 'vuecomponents/avatar_image.vue'
import BaseIcon from 'vuecomponents/base_icon.vue'
import SurfacePostComment from 'vuecomponents/SurfacePostComment.vue'

const OzBaseButton = defineAsyncComponent(() => import('library/v4/components/OzBaseButton.vue'))
const OzTextButton = defineAsyncComponent(() => import('library/v4/components/OzTextButton.vue'))
const OzIcon = defineAsyncComponent(() => import('library/v4/components/OzIcon.vue'))
const MultilineInput = defineAsyncComponent(() => import('vuecomponents/multiline_input.vue'))

const commentModule = namespace('comment')
const browser = namespace('browser')
const postAction = namespace('postAction')

@Component({
  components: {
    AvatarImage,
    BaseIcon,
    SurfacePostComment,
    MultilineInput,
    OzBaseButton,
    OzTextButton,
    OzIcon,
  },
})
export default class SurfacePostComments extends Vue {
  $refs!: {
    addCommentInput?: typeof MultilineInput
  }

  OzTextButtonColorScheme = OzTextButtonColorScheme

  @Getter colorScheme!: 'light' | 'dark'

  @Prop({ default: null }) post!: Post
  @Prop({ default: null }) user!: User
  @Prop({ default: null }) commentIds!: CommentId[]
  @Prop({ default: null }) comments!: { [id: number]: Comment }
  @Prop({ default: false }) isCommentable!: boolean
  @Prop({ default: null }) commentBeingEditedId!: CommentId

  @State isApp!: boolean
  @Getter isStream!: boolean
  @Getter dir!: string

  @commentModule.Getter newCommentBodyForPostCid
  @commentModule.Action startWritingNewComment
  @commentModule.Action stopWritingNewComment
  @commentModule.Action updateNewCommentBody
  @commentModule.Action updateInlineCommentInputLastTypedInMs

  @browser.Getter isPhone!: boolean

  @postAction.Getter expandedPostCid!: Cid

  get isNewPostExpandedPanelOpen(): boolean {
    return isAppUsing('expandedPostV1') && !!this.expandedPostCid && !this.isApp
  }

  get commentsList(): Comment[] {
    return this.commentIds.map((id) => this.comments[id])
  }

  get newCommentBody(): string {
    return this.newCommentBodyForPostCid(this.post.cid)
  }

  stopEditingComment(): void {
    this.$emit('stop-editing')
  }

  editComment($event): void {
    this.$emit('edit', $event)
  }

  publishComment(): void {
    if (!this.newCommentBody) return

    this.$emit('create', {
      postId: this.post.id,
      body: this.newCommentBody,
    })
    this.updateNewCommentBody({ postCid: this.post.cid, body: '' })
    this.$refs.addCommentInput?.focus()
  }

  submit(): void {
    this.publishComment()
  }

  onAddCommentClick(): void {
    this.startWritingNewComment({ postId: this.post.id })
  }

  onAddCommentBlur(): void {
    this.stopWritingNewComment({ postId: this.post.id })
  }

  onAddCommentInput(body): void {
    this.updateNewCommentBody({ postCid: this.post.cid, body })
    this.updateInlineCommentInputLastTypedInMs()
  }
}
